import { useState, useCallback, useEffect, useRef } from 'react';
import { LanguageCode } from '../types/types';
import supabaseProgressService from '../services/supabaseProgressService';
import { supabase } from '../services/supabaseAudioService';

// Sentence categories — keys with these as the 2nd segment are phrase entries
// and must never appear in the vocabulary revision queue
const SENTENCE_CATS = new Set([
  'Salutations', 'Présentation', 'Famille et relations',
  'Vie quotidienne', 'Au marché',
  'Voyages', 'Urgences et santé', 'Loisirs et culture'
]);

export interface RevisionWordInfo {
  word: string;
  category: string;
  isCorrect: boolean;
  timestamp: number;
  nextReview: number;
  interval: number;    
  easeFactor: number;
  translation?: string;
  audio?: string;
}

export interface GrammarWordInfo extends RevisionWordInfo {
  grammarType: 'rule' | 'conjugation' | 'vocabulary';
  subCategory?: string;
}

export interface SessionStats {
  total: number;
  correct: number;
  accuracy: number;
  isComplete: boolean;
}

export interface GrammarProgress {
  rules: Set<string>;
  lastReviewed: number | null;
}

export interface RevisionProgress {
  wordsToReview: Set<string>;
  sessionWords: RevisionWordInfo[];
  language: LanguageCode;
  grammarProgress?: GrammarProgress;
}

interface SessionState {
  isComplete: boolean;
  stats: SessionStats | null;
  reviewedWords?: RevisionWordInfo[];
}

interface SessionResult {
  stats: SessionStats;
  isComplete: boolean;
  showStats: boolean;
  reviewedWords: RevisionWordInfo[]; 
}

interface FinishSessionOptions {
  showStats?: boolean;
  lastWordInfo?: {
    word: string;
    category: string;
    isCorrect: boolean;
    timestamp: number;
    translation?: string;
    audio?: string;
  };
} 

const REVISION_STORAGE_PREFIX = 'revision';
const SESSION_HISTORY_KEY = 'sessionHistory';
const GRAMMAR_STORAGE_KEY = 'grammar';
const METADATA_STORAGE_KEY = 'metadata';

const cleanWordKey = (word: string): string => {
  // D'abord supprimer les parenthèses
  const withoutParentheses = word.replace(/\s*\([^)]*\)/g, '');
  // Puis appliquer le nettoyage existant si nécessaire
  return withoutParentheses.split(/[(:]/)[0].trim();
};

const createGrammarKey = (word: string, category: string, subCategory?: string): string => {
  const cleanWord = cleanWordKey(word);
  return `grammar:${category}:${subCategory || 'general'}:${cleanWord}`;
};

const createDefaultRevisionProgress = (languageCode: LanguageCode): RevisionProgress => ({
  wordsToReview: new Set<string>(),
  sessionWords: [],
  language: languageCode,
  grammarProgress: {
    rules: new Set<string>(),
    lastReviewed: null
  }
});

// ✅ NOUVELLE FONCTION: Sync Supabase en arrière-plan
const syncToSupabase = async (
  languageCode: LanguageCode,
  sessionHistory: RevisionWordInfo[],
  revisionProgress: RevisionProgress
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token || !session?.user) {
return false;
    }



    // Créer un objet de données de révision
    const revisionData = {
      sessionHistory: sessionHistory.map(word => ({
        word: word.word,
        category: word.category,
        isCorrect: word.isCorrect,
        timestamp: word.timestamp,
        nextReview: word.nextReview,
        interval: word.interval,
        easeFactor: word.easeFactor,
        translation: word.translation,
        audio: word.audio,
        // Preserve grammar fields so records reload with grammarType intact —
        // without these, 'grammarType' in record is false after reloading from
        // Supabase, causing grammar words to be keyed as vocabulary (wf:Grammaire:word)
        // instead of grammar (grammar:Grammaire:general:word), creating duplicates.
        ...('grammarType' in word ? {
          grammarType: (word as GrammarWordInfo).grammarType,
          subCategory: (word as GrammarWordInfo).subCategory
        } : {})
      })),
      wordsToReview: Array.from(revisionProgress.wordsToReview),
      grammarProgress: revisionProgress.grammarProgress ? {
        rules: Array.from(revisionProgress.grammarProgress.rules),
        lastReviewed: revisionProgress.grammarProgress.lastReviewed
      } : undefined,
      lastUpdated: Date.now()
    };

   

    // Vérifier si une ligne existe déjà (évite le besoin de contrainte UNIQUE)
    const { data: existing, error: selectError } = await supabase
      .from('user_revision')
      .select('user_id')
      .eq('user_id', session.user.id)
      .eq('language_code', languageCode)
      .maybeSingle();

    if (selectError) {
return false;
    }

    let saveError;
    if (existing) {
      // UPDATE : ligne existante
      const { error } = await supabase
        .from('user_revision')
        .update({
          revision_data: revisionData,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode);
      saveError = error;
    } else {
      // INSERT : première sauvegarde
      const { error } = await supabase
        .from('user_revision')
        .insert({
          user_id: session.user.id,
          language_code: languageCode,
          revision_data: revisionData,
          updated_at: new Date().toISOString()
        });
      saveError = error;
    }

    if (saveError) {
if (saveError.message?.includes('relation') || saveError.message?.includes('does not exist')) {
}
      return false;
    }

    return true;
  } catch (error) {
if (error instanceof Error) {
    }
    return false;
  }
};

// ✅ NOUVEAU: Fonction pour charger l'historique depuis Supabase
const loadFromSupabase = async (languageCode: LanguageCode): Promise<{
  sessionHistory: RevisionWordInfo[];
  wordsToReview: Set<string>;
} | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token || !session?.user) {
      return null;
    }



    const LOAD_TIMEOUT_MS = 10000;
    const timeoutPromise = new Promise<null>(resolve =>
      setTimeout(() => resolve(null), LOAD_TIMEOUT_MS)
    );

    const queryResult = await Promise.race([
      supabase
        .from('user_revision')
        .select('revision_data')
        .eq('user_id', session.user.id)
        .eq('language_code', languageCode)
        .maybeSingle()
        .then(r => r as any),
      timeoutPromise
    ]);

    if (!queryResult) return null; // timeout

    const { data, error } = queryResult as { data: any; error: any };

    if (error) {
return null;
    }

    if (!data || !data.revision_data) {
return null;
    }

    const revisionData = data.revision_data as any;
return {
      sessionHistory: revisionData.sessionHistory || [],
      wordsToReview: new Set(revisionData.wordsToReview || [])
    };
  } catch (error) {
return null;
  }
};

export const useRevisionProgress = (languageCode: LanguageCode, userId?: string) => {
  const progressKey = `${REVISION_STORAGE_PREFIX}-${languageCode}-progress`;
  const historyKey = `${REVISION_STORAGE_PREFIX}-${languageCode}-${SESSION_HISTORY_KEY}`;
  const grammarKey = `${REVISION_STORAGE_PREFIX}-${languageCode}-${GRAMMAR_STORAGE_KEY}`;
  const pendingSave = useRef<NodeJS.Timeout | null>(null);

  const [isLoadingProgress, setIsLoadingProgress] = useState(true);

  const [revisionProgress, setRevisionProgress] = useState<RevisionProgress>(() =>
    createDefaultRevisionProgress(languageCode)
  );
  // Ref kept in sync with revisionProgress — updated synchronously during batch calls
  // so that successive updateRevisionProgress calls chain correctly in React 18 batching.
  const revisionProgressRef = useRef<RevisionProgress>(createDefaultRevisionProgress(languageCode));
  revisionProgressRef.current = revisionProgress; // updated on every render
  
  const [sessionState, setSessionState] = useState<SessionState>({
    isComplete: false,
    stats: null
  });
  
  const [currentSessionWords, setCurrentSessionWords] = useState<RevisionWordInfo[]>([]);
  // Ref kept in sync so finishSession can read current session words without stale closure
  const currentSessionWordsRef = useRef<RevisionWordInfo[]>([]);

  const loadSessionHistory = useCallback((): RevisionWordInfo[] => {
    try {
      const saved = localStorage.getItem(historyKey);
      if (!saved) return [];
      
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
return [];
    }
  }, [historyKey]);

  const saveSessionHistory = useCallback((newHistory: RevisionWordInfo[]) => {
    try {
      const existingHistory = loadSessionHistory();

      const uniqueNewWords = newHistory.filter(newWord =>
        !existingHistory.some(existingWord =>
          existingWord.word === newWord.word &&
          existingWord.category === newWord.category &&
          Math.abs(existingWord.timestamp - newWord.timestamp) < 1000
        )
      );

      const mergedHistory = [...uniqueNewWords, ...existingHistory]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 1000);

      localStorage.setItem(historyKey, JSON.stringify(mergedHistory));

      // Lire localStorage au moment du sync (pas mergedHistory capturé à l'appel)
      // pour obtenir l'historique le plus récent si plusieurs appels rapides.
      // revisionProgressRef.current garantit le wordsToReview le plus récent.
      setTimeout(() => {
        const latestHistory = loadSessionHistory();
        syncToSupabase(languageCode, latestHistory, revisionProgressRef.current);
      }, 100);

      return mergedHistory;
    } catch (error) {
return loadSessionHistory();
    }
  }, [historyKey, loadSessionHistory, languageCode]);

  const saveProgress = useCallback(async (progress: RevisionProgress, shouldSaveHistory: boolean = false) => {
    try {
      const dataToSave = {
        ...progress,
        wordsToReview: Array.from(progress.wordsToReview),
        timestamp: Date.now()
      };
      
      // ✅ ÉTAPE 1: localStorage (instantané)
      localStorage.setItem(progressKey, JSON.stringify(dataToSave));
      
      if (progress.grammarProgress) {
        localStorage.setItem(grammarKey, JSON.stringify({
          rules: Array.from(progress.grammarProgress.rules),
          lastReviewed: progress.grammarProgress.lastReviewed
        }));
      }
      
      if (shouldSaveHistory && currentSessionWords.length > 0) {
        saveSessionHistory(currentSessionWords);
      }
      
      // ✅ ÉTAPE 2: Supabase (arrière-plan)
      // Utiliser revisionProgressRef.current pour le wordsToReview le plus récent
      setTimeout(() => {
        const sessionHistory = loadSessionHistory();
        syncToSupabase(languageCode, sessionHistory, revisionProgressRef.current);
      }, 100);
      
    } catch (error) {
throw error;
    }
  }, [progressKey, grammarKey, currentSessionWords, saveSessionHistory, languageCode, loadSessionHistory]);

  // ✅ NOUVELLE FONCTION: Recalculer les mots dus
  const recalculateWordsToReview = useCallback(async (): Promise<Set<string>> => {
    try {
      const history = loadSessionHistory();
      const now = Date.now();
      const dueWords = new Set<string>();

      // Grouper par mot unique (garder seulement la révision la plus récente)
      const wordMap = new Map<string, RevisionWordInfo>();

      history.forEach(record => {
        // Skip sentence categories — they have their own revision system
        if (SENTENCE_CATS.has(record.category)) return;
        // Créer la clé selon le type (grammaire ou vocabulaire)
        // Always use undefined (→ 'general') for grammar subCategory so all records
        // for the same word map to the same key regardless of subCategory stored.
        const isGrammar = 'grammarType' in record;
        const key = isGrammar
          ? createGrammarKey(record.word, record.category, undefined)
          : `${languageCode}:${record.category}:${cleanWordKey(record.word)}`;

        // Garder seulement la révision la plus récente pour chaque mot
        const existing = wordMap.get(key);
        if (!existing || existing.timestamp < record.timestamp) {
          wordMap.set(key, record);
        }
      });
      
     
      
     

      // Vérifier quels mots sont dus
      wordMap.forEach((record, key) => {
        // Ajouter si nextReview est passé (ou absent) — qu'il soit correct ou non.
        // Les mots incorrects ont nextReview=now, donc ils sont immédiatement dus.
        if (!record.nextReview || record.nextReview <= now) {
          dueWords.add(key);
        }
      });
      // Ajouter les mots de grammaire appris mais jamais révisés (pas dans l'historique)
      try {
        const grammarStorageKey = `grammar-progress-${languageCode}`;
        const savedGrammarProgress = localStorage.getItem(grammarStorageKey);
        if (savedGrammarProgress) {
          const grammarProgList = JSON.parse(savedGrammarProgress);
          grammarProgList.forEach((categoryProgress: any) => {
            categoryProgress.masteredWords?.forEach((wordData: any) => {
              const key = createGrammarKey(wordData.word, 'Grammaire', undefined);
              if (!wordMap.has(key)) {
                dueWords.add(key);
              }
            });
          });
        }
      } catch (_) {}

      // Ajouter les mots de vocabulaire en attente de première révision
      // (dans wordsToReview localStorage mais sans historique de session)
      try {
        const savedProgress = localStorage.getItem(progressKey);
        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          const storedKeys: string[] = parsed.wordsToReview || [];
          storedKeys.forEach((key: string) => {
            if (key.startsWith('grammar:')) return; // déjà géré ci-dessus
            const parts = key.split(':');
            if (parts.length >= 3 && SENTENCE_CATS.has(parts[1])) return;
            if (!wordMap.has(key)) {
              dueWords.add(key);
            }
          });
        }
      } catch (_) {}

    
      return dueWords;

    } catch (error) {
      return new Set<string>();
    }
  }, [loadSessionHistory, languageCode, progressKey]);

  const updateRevisionProgress = useCallback(async (
    word: string,
    isCorrect: boolean,
    category: string,
    options?: {
      isGrammar?: boolean;
      subCategory?: string;
      grammarType?: 'rule' | 'conjugation' | 'vocabulary';
      translation?: string;
      audio?: string;
    }
  ) => {
    const now = Date.now();
    const history = loadSessionHistory();
    const previousReviews = history
      .filter(item => {
        if (options?.isGrammar) {
          return item.word === word && 
                 item.category === category &&
                 ('grammarType' in item && (item as GrammarWordInfo).grammarType === options.grammarType);
        }
        return item.word === word && item.category === category;
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  
    let interval = 1;
    let easeFactor = 2.5;
    // Incorrect sans historique → immédiatement disponible
    let nextReviewDate = now;

    if (previousReviews.length > 0) {
      const lastReview = previousReviews[0];
      interval = lastReview.interval || 1;
      easeFactor = lastReview.easeFactor || 2.5;

      if (isCorrect) {
        interval = Math.round(interval * easeFactor);
        easeFactor = Math.max(1.3, easeFactor + 0.1);
        // Correct → programmer la prochaine révision dans le futur
        nextReviewDate = now + (interval * 24 * 60 * 60 * 1000);
      } else {
        interval = 1;
        easeFactor = Math.max(1.3, easeFactor - 0.2);
        // Incorrect → immédiatement disponible pour réessayer (nextReview = now)
        nextReviewDate = now;
      }
    } else if (isCorrect) {
      // Première révision correcte → programmer dans 1 jour
      nextReviewDate = now + (interval * 24 * 60 * 60 * 1000);
    }
  
    const wordInfo: RevisionWordInfo = {
      word,
      category,
      isCorrect,
      timestamp: now,
      nextReview: nextReviewDate,
      interval,
      easeFactor,
      translation: options?.translation,
      audio: options?.audio
    };
  
    if (options?.isGrammar) {
      (wordInfo as GrammarWordInfo).grammarType = options.grammarType || 'rule';
      (wordInfo as GrammarWordInfo).subCategory = options.subCategory;
    }
  
    setCurrentSessionWords(prev => {
      const newWords = [...prev];
      const existingIndex = newWords.findIndex(item =>
        item.word === word &&
        item.category === category &&
        (options?.isGrammar ?
          (item as GrammarWordInfo).grammarType === options.grammarType : true)
      );

      if (existingIndex >= 0) {
        newWords[existingIndex] = wordInfo;
      } else {
        newWords.push(wordInfo);
      }

      currentSessionWordsRef.current = newWords;
      return newWords;
    });
  
    // Compute wordKey — always normalize grammar subCategory to undefined ('general')
    // to stay consistent with addWordsToReview and recalculateWordsToReview.
    // Using the actual subCategory here would create a second key that coexists
    // with the 'general' key, inflating the wordsToReview count.
    const wordKey = options?.isGrammar
      ? createGrammarKey(word, category, undefined)
      : `${languageCode}:${category}:${cleanWordKey(word)}`;
  

    // Build progressToSave from the ref (synchronous, chains correctly across batched calls)
    const current = revisionProgressRef.current;
    const newWordsToReview = new Set(current.wordsToReview);
    let newGrammarProgress = current.grammarProgress;

    // Helper: for grammar words the subCategory in the stored key may differ from the one
    // used at save time (it's always undefined/general when coming from handleSessionSave).
    // So we do an exact delete first, then a fuzzy scan on word suffix as fallback.
    const deleteGrammarKey = (set: Set<string>, key: string): void => {
      if (set.delete(key)) return;
      if (options?.isGrammar) {
        const cleanW = cleanWordKey(word);
        const prefix = `grammar:${category}:`;
        const suffix = ':' + cleanW;
        for (const k of Array.from(set)) {
          if (k.startsWith(prefix) && k.endsWith(suffix)) {
            set.delete(k);
            break;
          }
        }
      }
    };

    if (isCorrect) {
      deleteGrammarKey(newWordsToReview, wordKey);
      if (options?.isGrammar && newGrammarProgress) {
        const newRules = new Set(newGrammarProgress.rules);
        deleteGrammarKey(newRules, wordKey);
        newGrammarProgress = { ...newGrammarProgress, rules: newRules, lastReviewed: now };
      }
    } else {
      // Remove any old grammar key for this word before adding the new one
      if (options?.isGrammar) deleteGrammarKey(newWordsToReview, wordKey);
      newWordsToReview.add(wordKey);
      if (options?.isGrammar && newGrammarProgress) {
        const newRules = new Set(newGrammarProgress.rules);
        deleteGrammarKey(newRules, wordKey);
        newRules.add(wordKey);
        newGrammarProgress = { ...newGrammarProgress, rules: newRules, lastReviewed: now };
      }
    }

    const progressToSave: RevisionProgress = { ...current, wordsToReview: newWordsToReview, grammarProgress: newGrammarProgress };
    

    // Update the ref synchronously so the next call in the same batch sees the updated state
    revisionProgressRef.current = progressToSave;

    // Schedule React state update (batched — UI will reflect all changes after the batch)
    setRevisionProgress(progressToSave);

    await saveSessionHistory([wordInfo]);
    await saveProgress(progressToSave, false);

}, [languageCode, loadSessionHistory, saveSessionHistory, saveProgress]);

  // Dans useRevisionProgress.tsx
  const addWordsToReview = useCallback(async (words: Array<{
    word: string;
    category: string;
    isGrammar?: boolean;
    subCategory?: string;
    grammarType?: 'rule' | 'conjugation' | 'vocabulary';
    translation?: string;
    audio?: string;
  }>) => {

    // Créer un tableau pour stocker toutes les clés à ajouter
    const keysToAdd: string[] = [];

    words.forEach(({ word, category, isGrammar, subCategory, grammarType }) => {
      let wordKey: string;

      if (isGrammar) {
        wordKey = createGrammarKey(word, category, undefined);
      } else {
        wordKey = `${languageCode}:${category}:${cleanWordKey(word)}`;
      }


      keysToAdd.push(wordKey);
    });

// Utiliser une mise à jour fonctionnelle pour garantir l'état le plus récent
    const newProgress = await new Promise<RevisionProgress>((resolve) => {
      setRevisionProgress(prev => {
        // Créer une copie de l'ensemble existant
        const newWordsToReview = new Set(prev.wordsToReview);

        // Ajouter chaque clé à l'ensemble
        keysToAdd.forEach(key => newWordsToReview.add(key));

const updatedProgress = {
          ...prev,
          wordsToReview: newWordsToReview
        };

        // Résoudre la promesse avec le nouvel état
        resolve(updatedProgress);

        return updatedProgress;
      });
    });

    // Sauvegarder immédiatement les changements
    await saveProgress(newProgress);

    // Forcer un rafraîchissement de l'interface utilisateur
    setTimeout(() => {
      // Cette mise à jour ne change rien mais force un re-rendu
      setRevisionProgress(current => ({...current}));
    }, 100);

}, [languageCode, saveProgress]);

  const calculateSessionStats = useCallback((): SessionStats => {
    const total = currentSessionWords.length;
    const correct = currentSessionWords.filter(w => w.isCorrect).length;
    const accuracy = total > 0 ? (correct / total) * 100 : 0;
    
    return {
      total,
      correct,
      accuracy,
      isComplete: false
    };
  }, [currentSessionWords]);

  const finishSession = useCallback(async (options?: FinishSessionOptions): Promise<SessionResult | null> => {
    if (!sessionState.isComplete) {
      try {
        // Use the ref — always up-to-date even inside React 18 batched updates
        let finalSessionWords: RevisionWordInfo[] = [...currentSessionWordsRef.current];
        if (finalSessionWords.length === 0 && options?.lastWordInfo) {
          finalSessionWords = [{ ...options.lastWordInfo, interval: 1, easeFactor: 2.5,
            nextReview: options.lastWordInfo.timestamp + (24 * 60 * 60 * 1000) } as RevisionWordInfo];
        }

        const finalStats = {
          total: finalSessionWords.length,
          correct: finalSessionWords.filter(w => w.isCorrect).length,
          accuracy: finalSessionWords.length > 0 ?
            (finalSessionWords.filter(w => w.isCorrect).length / finalSessionWords.length) * 100 : 0,
          isComplete: true
        };

        // Individual updateRevisionProgress calls already saved progress to localStorage correctly.
        // Only save session history here (deduplication handles any overlap).
        if (options?.lastWordInfo) {
          const lastWord = {
            ...options.lastWordInfo,
            interval: 1,
            easeFactor: 2.5,
            nextReview: options.lastWordInfo.timestamp + (24 * 60 * 60 * 1000)
          } as RevisionWordInfo;
          saveSessionHistory([lastWord]);
        }

        setSessionState({
          isComplete: true,
          stats: finalStats,
          reviewedWords: finalSessionWords
        });

        return {
          stats: finalStats,
          isComplete: true,
          showStats: options?.showStats ?? true,
          reviewedWords: finalSessionWords
        };
      } catch (error) {
throw error;
      }
    }
    return null;
  }, [sessionState.isComplete, saveSessionHistory]);

  const getSessionWords = useCallback((): RevisionWordInfo[] => {
    return currentSessionWords;
  }, [currentSessionWords]);

  const resetSession = useCallback(() => {
    setSessionState({
      isComplete: false,
      stats: null
    });
    setCurrentSessionWords([]);
    currentSessionWordsRef.current = [];
  }, []);

  const loadProgressFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(progressKey);
      if (saved) {
        const parsed = JSON.parse(saved);
       
        const loaded: RevisionProgress = {
          ...parsed,
          wordsToReview: new Set(
            (parsed.wordsToReview || []).filter((key: string) => {
              const parts = key.split(':');
              if (parts.length < 3) return true;
              return !SENTENCE_CATS.has(parts[1]);
            })
          ),
          language: languageCode
        };
       
        const savedGrammar = localStorage.getItem(grammarKey);
        if (savedGrammar) {
          const grammarData = JSON.parse(savedGrammar);
          loaded.grammarProgress = {
            rules: new Set(grammarData.rules || []),
            lastReviewed: grammarData.lastReviewed || null
          };
        }
        
        // Fusion avec l'état en cours pour préserver les mots ajoutés par
        // addWordsToReview pendant le chargement (race condition protection)
        setRevisionProgress(prev => ({
          ...loaded,
          wordsToReview: new Set(
            [...prev.wordsToReview, ...loaded.wordsToReview].filter((key: string) => {
              const parts = key.split(':');
              return parts.length < 3 || !SENTENCE_CATS.has(parts[1]);
            })
          ),
        }));
      }
    } catch (error) {
}
  }, [progressKey, grammarKey, languageCode]);

  // ✅ MODIFIÉ: Charger depuis Supabase au montage + recalculer les mots dus
  useEffect(() => {
    const loadInitialData = async () => {
      // Réinitialiser l'état pour éviter que des données d'un ancien compte
      // restent visibles pendant le chargement (déclenché par changement de userId).
      setIsLoadingProgress(true);
      setRevisionProgress(createDefaultRevisionProgress(languageCode));

      try {
// 1. Charger localStorage d'abord (rapide)
      loadProgressFromStorage();
// 2. Tenter de charger depuis Supabase (en arrière-plan)
      const supabaseData = await loadFromSupabase(languageCode);

      if (supabaseData) {
      
        // Fusionner sessionHistory avec localStorage
        const localHistory = loadSessionHistory();
        const mergedHistory = [...localHistory];

        // Ajouter les entrées Supabase qui ne sont pas déjà dans local
        supabaseData.sessionHistory.forEach(supabaseWord => {
          const exists = localHistory.some(localWord =>
            localWord.word === supabaseWord.word &&
            localWord.category === supabaseWord.category &&
            Math.abs(localWord.timestamp - supabaseWord.timestamp) < 10000 // 10 secondes de tolérance
          );

          if (!exists) {
            mergedHistory.push(supabaseWord);
          }
        });

        // Trier par timestamp (plus récent d'abord)
        mergedHistory.sort((a, b) => b.timestamp - a.timestamp);

        // Sauvegarder le merged history dans localStorage
        if (mergedHistory.length > localHistory.length) {
          localStorage.setItem(historyKey, JSON.stringify(mergedHistory.slice(0, 1000)));
}

        // ✅ Recalculer TOUS les mots dus depuis l'historique
        const now = Date.now();
        const validWordsToReview = new Set<string>();

        // Grouper par mot unique (clé normalisée : grammar → toujours 'general')
        const wordMap = new Map<string, RevisionWordInfo>();
        mergedHistory.forEach(record => {
          // Skip sentence categories — they have their own revision system
          if (SENTENCE_CATS.has(record.category)) return;
          const isGrammar = 'grammarType' in record;
          // Always use undefined (→ 'general') for grammar so all records for the
          // same word collapse to the same key regardless of stored subCategory.
          const key = isGrammar
            ? createGrammarKey(record.word, record.category, undefined)
            : `${languageCode}:${record.category}:${cleanWordKey(record.word)}`;

          const existing = wordMap.get(key);
          if (!existing || existing.timestamp < record.timestamp) {
            wordMap.set(key, record);
          }
        });

        // Vérifier quels mots sont dus
        // Un mot est dû si nextReview est absent ou passé — correct ou non.
        // Les mots incorrects ont nextReview=now → immédiatement disponibles.
        wordMap.forEach((record, key) => {
          if (!record.nextReview || record.nextReview <= now) {
            validWordsToReview.add(key);
          }
        });

        // Construire l'ensemble des clés à NE PAS réintroduire :
        // mots révisés correctement dont la prochaine révision est dans le futur.
        const notDueKeys = new Set<string>();
        wordMap.forEach((record, key) => {
          if (record.isCorrect && record.nextReview > now) {
            notDueKeys.add(key);
          }
        });

       

        supabaseData.wordsToReview.forEach(wordKey => {
          // Normaliser les clés de grammaire Supabase vers 'general' pour qu'elles
          // correspondent aux clés locales normalisées.
          let normalizedKey = wordKey;
          if (wordKey.startsWith('grammar:')) {
            const parts = wordKey.split(':');
            if (parts.length >= 4) {
              // grammar:category:subCategory:cleanWord → grammar:category:general:cleanWord
              normalizedKey = `grammar:${parts[1]}:general:${parts.slice(3).join(':')}`;
            }
          }

          // Skip sentence categories
          const kParts = normalizedKey.split(':');
          if (kParts.length >= 3 && SENTENCE_CATS.has(kParts[1])) return;

          // N'ajouter que si le mot n'est pas déjà en attente future dans le wordMap
          if (!notDueKeys.has(normalizedKey)) {
            validWordsToReview.add(normalizedKey);
          }
        });

// ✅ FIX Bug 1: Fusionner avec prev.wordsToReview pour préserver les mots
        // nouvellement appris (ajoutés par addWordsToReview) qui ne sont pas encore
        // dans l'historique Supabase. On exclut les mots déjà correctement révisés.
        setRevisionProgress(prev => {
          const merged = new Set(validWordsToReview);
          // Préserver les clés locales (ex: mots appris récemment via addWordsToReview)
          // sauf celles qui correspondent à des mots correctement révisés
          prev.wordsToReview.forEach(key => {
            if (!notDueKeys.has(key)) {
              merged.add(key);
            } else {
            }
          });
         
          return { ...prev, wordsToReview: merged };
        });

      } else {
// ✅ Même en mode local, recalculer les mots dus
        const dueWords = await recalculateWordsToReview();

        if (dueWords.size > 0) {
          setRevisionProgress(prev => ({
            ...prev,
            wordsToReview: new Set([...prev.wordsToReview, ...dueWords])
          }));
        }
      }

      } catch (_err) {
        // Erreur inattendue dans loadInitialData — on débloque le spinner quoi qu'il arrive.
      } finally {
        setIsLoadingProgress(false);
      }
    };

    loadInitialData();
  }, [languageCode, userId, loadProgressFromStorage, loadSessionHistory, historyKey, recalculateWordsToReview]);

  // Recharge depuis localStorage + recalcule les mots dus (à appeler au retour au menu).
  // validCategoryNames : noms des catégories réelles de la langue — permet de filtrer les clés
  // avec une catégorie fantôme (ex: wf:General:mot) qui gonfleraient le badge.
  const refreshRevisionProgress = useCallback(async (validCategoryNames?: Set<string>) => {
    loadProgressFromStorage();
    const dueWords = await recalculateWordsToReview();

    // Calculer les clés des mots correctement révisés avec nextReview dans le futur
    // (ces mots doivent être retirés du compteur)
    const now = Date.now();
    const history = loadSessionHistory();
    const latestMap = new Map<string, RevisionWordInfo>();
    history.forEach(r => {
      const isGrammar = 'grammarType' in r;
      const key = isGrammar
        ? createGrammarKey(r.word, r.category, undefined)
        : `${languageCode}:${r.category}:${cleanWordKey(r.word)}`;
      const ex = latestMap.get(key);
      if (!ex || ex.timestamp < r.timestamp) latestMap.set(key, r);
    });
    const doneKeys = new Set<string>();
    latestMap.forEach((r, key) => {
      if (r.isCorrect && r.nextReview > now) doneKeys.add(key);
    });

    // Calculer le Set final : uniquement dueWords minus doneKeys.
    // recalculateWordsToReview lit déjà localStorage.wordsToReview (mots sans
    // historique) donc ré-ajouter prev.wordsToReview était redondant et gonflait
    // le badge avec des entrées obsolètes (ex. doublons clé grammaire/vocabulaire).
    // Si validCategoryNames est fourni, on exclut aussi les clés dont la catégorie
    // n'existe pas dans la langue (ex: wf:General:mot issu d'anciens bugs).
    const cleaned = new Set<string>();
    dueWords.forEach(key => {
      if (doneKeys.has(key)) return;
      if (validCategoryNames && !key.startsWith('grammar:')) {
        const parts = key.split(':');
        // format: languageCode:categoryName:word — catégorie en position 1
        if (parts.length >= 3 && !validCategoryNames.has(parts[1])) return;
      }
      cleaned.add(key);
    });

    setRevisionProgress(prev => ({ ...prev, wordsToReview: cleaned }));

    // Persister en localStorage pour que la prochaine instance du hook lise
    // les données nettoyées (RevisionMode crée sa propre instance du hook).
    try {
      const saved = localStorage.getItem(progressKey);
      const base = saved ? JSON.parse(saved) : {};
      localStorage.setItem(progressKey, JSON.stringify({
        ...base,
        wordsToReview: Array.from(cleaned),
        timestamp: Date.now()
      }));
    } catch (_) {}

  }, [loadProgressFromStorage, recalculateWordsToReview, loadSessionHistory, languageCode, progressKey]);

  // Synchronise wordsToReview avec la liste des mots appris pour combler l'écart
  // entre wordsToReview.size (badge) et getDueWordsForCategory (RevisionMode).
  // Les mots appris (recentlyLearnedWords) mais absents de wordsToReview sont ajoutés,
  // sauf ceux déjà correctement révisés (nextReview dans le futur).
  const syncWithLearnedWords = useCallback((
    learnedWords: Array<{ word: string; category: string; isGrammar?: boolean }>
  ) => {
    const history = loadSessionHistory();
    const now = Date.now();

    // Calculer les clés déjà révisées correctement (ne pas les réajouter)
    const wordMap = new Map<string, { isCorrect: boolean; nextReview?: number; timestamp: number }>();
    history.forEach(record => {
      if (SENTENCE_CATS.has(record.category)) return;
      const isGrammar = 'grammarType' in record;
      const key = isGrammar
        ? createGrammarKey(record.word, record.category, undefined)
        : `${languageCode}:${record.category}:${cleanWordKey(record.word)}`;
      const existing = wordMap.get(key);
      if (!existing || existing.timestamp < (record as any).timestamp) {
        wordMap.set(key, record as any);
      }
    });

    const notDueKeys = new Set<string>();
    wordMap.forEach((record, key) => {
      if (record.isCorrect && record.nextReview && record.nextReview > now) {
        notDueKeys.add(key);
      }
    });

    setRevisionProgress(prev => {
      const newWordsToReview = new Set(prev.wordsToReview);
      learnedWords.forEach(({ word, category, isGrammar }) => {
        if (SENTENCE_CATS.has(category)) return;
        const key = isGrammar
          ? createGrammarKey(word, category, undefined)
          : `${languageCode}:${category}:${cleanWordKey(word)}`;
        if (!notDueKeys.has(key)) {
          newWordsToReview.add(key);
        }
      });
      if (newWordsToReview.size === prev.wordsToReview.size) return prev;
      return { ...prev, wordsToReview: newWordsToReview };
    });
  }, [languageCode, loadSessionHistory]);

  return {
    revisionProgress,
    isLoadingProgress,
    sessionState,
    currentSessionWords,
    updateRevisionProgress,
    addWordsToReview,
    syncWithLearnedWords,
    getSessionWords,
    calculateSessionStats,
    finishSession,
    resetSession,
    loadSessionHistory,
    recalculateWordsToReview,
    refreshRevisionProgress,
  };
};